import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { AccentPicker } from './AccentPicker';
import { DecorationPicker } from './DecorationPicker';
import { ImagePicker } from './ImagePicker';
import { ACCENT_PRESETS, DECORATIONS } from './bannerOptions';
import { FieldLabel } from './bannerFormBits';
import type { BannerComposer } from './useBannerComposer';

/** Task activity's card style: one line saying what is set, opening to every option. */
export function CardLook({ composer }: { composer: BannerComposer }) {
  const { annAccent, setAnnAccent, annDecoration, setAnnDecoration, imagePreview, imageAllowed } = composer;
  const [open, setOpen] = useState(false);
  const accentName =
    ACCENT_PRESETS.find((c) => c.value.toLowerCase() === annAccent.toLowerCase())?.label ?? annAccent;
  const decoration =
    annDecoration === 'none' ? 'No decoration' : DECORATIONS.find((d) => d.value === annDecoration)?.label;

  return (
    <div>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex min-h-[38px] w-full items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-left text-sm hover:bg-gray-50"
      >
        <span className="whitespace-nowrap font-medium text-gray-700">Card look</span>
        <span className="h-3 w-3 flex-none rounded-full" style={{ backgroundColor: annAccent }} />
        <span className="min-w-0 flex-1 truncate text-gray-500">
          {accentName} · {decoration} · {imagePreview ? 'Image added' : 'No image'}
        </span>
        <ChevronDown className={`h-4 w-4 flex-none text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="mt-3 flex flex-col gap-4">
          <div>
            <FieldLabel
              text="Decoration"
              hint="Animated particles that drift across the card. Pick None for anything serious."
            />
            <DecorationPicker compact value={annDecoration} onChange={setAnnDecoration} />
          </div>
          <AccentPicker value={annAccent} onChange={setAnnAccent} />
          {imageAllowed ? (
            <ImagePicker composer={composer} />
          ) : (
            <p className="text-sm text-gray-500">No image on a consent: its record keeps only the words.</p>
          )}
        </div>
      )}
    </div>
  );
}
